import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { Dispatch } from 'react';
import type { AgentModelSupportHelpers } from '@/hooks/config/useAgentModelSupport';
import type { AgentType } from '@/lib/types';
import type { LaunchFormAction } from './form-state';
import type { UseLaunchAgentDefaultsResult } from './useLaunchAgentDefaults';

export interface UseLaunchAgentSyncOptions {
  open: boolean;
  isMatrixDriven: boolean;
  modelSupport: AgentModelSupportHelpers & {
    isLoading: boolean;
    defaultAgentType: string | undefined;
    launchableAgents?: Array<{ agentType: string; enabled: boolean }>;
  };
  state: { agentType: AgentType; model: string; selectedProviderId?: string | null };
  modelOptions: Array<{ id: string }>;
  effectiveProviderId: string | null;
  defaults: UseLaunchAgentDefaultsResult;
  dispatch: Dispatch<LaunchFormAction>;
  selectedPersonaId?: string | null;
  /**
   * When true, the form was seeded with an explicit agent type from the
   * caller (e.g. "Launch again" pre-fills from a prior agent). Skip the
   * admin-default sync so the seeded {@link state.agentType} — and the
   * model/endpoint that hang off it — aren't overwritten on the first render.
   *
   * Restoring last-launch config from localStorage or a server draft is the
   * same situation, except that the remembered type may since have been
   * disabled. Call {@link UseLaunchAgentSyncResult.markRestoredAgentType}
   * before applying it so this hook can preserve enabled remembered types and
   * fall back to the admin default for disabled ones.
   */
  seededAgentType?: boolean;
}

export interface UseLaunchAgentSyncResult {
  markUserPickedAgentType: () => void;
  markRestoredAgentType: (agentType: AgentType) => void;
  resetAgentTypePreference: () => void;
}

/**
 * Manages form state synchronization with admin-configured defaults and model list changes.
 *
 * This hook handles two distinct synchronization concerns:
 *
 * 1. **Admin Default Sync** (PRD-25): When the dialog opens and the user hasn't explicitly
 *    picked a different agent type, sync the form to the admin-configured default. This
 *    also runs when support data arrives late, allowing the default to "land" in an
 *    already-open dialog.
 *
 * 2. **Model Options Sync**: When the model options list changes (e.g., switching agent types
 *    or providers), ensure the selected model is valid. If invalid, select a sensible default.
 *    Also handles edge cases like when the current provider has no available endpoints.
 */
export function useLaunchAgentSync({
  open,
  isMatrixDriven,
  modelSupport,
  state,
  modelOptions,
  effectiveProviderId,
  defaults,
  dispatch,
  selectedPersonaId,
  seededAgentType,
}: UseLaunchAgentSyncOptions): UseLaunchAgentSyncResult {
  // Tracks whether the user has actively touched the agent-type picker. When
  // pristine, we allow the admin-configured default to flow through as soon as
  // it loads, even after the dialog has already opened (PRD-25 remediation).
  const userPickedAgentTypeRef = useRef(false);
  // A restored preference is user-owned only while that type is still allowed
  // by the current org. Keep it pending while support loads so a valid saved
  // type survives late data without letting a disabled one bypass policy.
  const restoredAgentTypeRef = useRef<AgentType | null>(null);
  // Hydration can restore the same type as the reducer's initial state, so the
  // state.agentType dependency alone does not guarantee a validation pass.
  const [restoredAgentTypeVersion, bumpRestoredAgentTypeVersion] = useReducer(
    (version: number) => version + 1,
    0,
  );
  // Remember the previous `open` state so we only reset on a close→open edge.
  const prevOpenRef = useRef(open);

  const {
    getDefaultProviderForAgentType,
    getDefaultModelForAgentType,
    getDefaultEndpointForModel,
  } = defaults;

  // Sync the form with the admin-configured default when (a) the dialog opens
  // and (b) the user hasn't explicitly picked a different agent type. Runs on
  // every relevant change so a late-arriving support payload still lands in the
  // dialog (PRD-25 remediation).
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      userPickedAgentTypeRef.current = false;
      restoredAgentTypeRef.current = null;
      return;
    }
    const opening = !prevOpenRef.current;
    prevOpenRef.current = true;

    // "Launch again" (and other seeded-default callers) pass the prior
    // agent's type in via `defaults?.agentType` so it lands in the reducer's
    // initial state. Without this guard the admin-default sync fires on the
    // first open and clobbers it — and because `set_agent_type` resets model
    // + endpoint together, the dialog ends up filled with the org default
    // instead of the agent the user clicked "Launch again" on.
    if (seededAgentType) {
      userPickedAgentTypeRef.current = true;
      return;
    }
    if (modelSupport.isLoading) return;
    const restoredAgentType = restoredAgentTypeRef.current;
    let restoredAgentTypeWasDisabled = false;
    if (restoredAgentType != null) {
      const restoredLaunchableEntry = modelSupport.launchableAgents?.find(
        (entry) => entry.agentType === restoredAgentType,
      );
      const restoredAgentTypeEnabled = modelSupport.launchableAgents
        ? restoredLaunchableEntry?.enabled === true
        : modelSupport.isAgentTypeEnabled(restoredAgentType);
      if (restoredAgentTypeEnabled) {
        // Keep restored preferences distinct from explicit picks so every
        // support refresh revalidates them against current org policy.
        return;
      } else {
        restoredAgentTypeRef.current = null;
        restoredAgentTypeWasDisabled = true;
      }
    }
    if (userPickedAgentTypeRef.current && !opening) return;
    if (selectedPersonaId != null && !opening && !restoredAgentTypeWasDisabled) return;

    const configured = modelSupport.defaultAgentType as AgentType | undefined;
    if (!configured) return;
    if (!modelSupport.isAgentTypeEnabled(configured)) return;
    if (!opening && configured === state.agentType) return;

    if (restoredAgentTypeWasDisabled && selectedPersonaId != null) {
      // The restored persona was configured for the disabled agent type. Clear
      // it along with the override so the UI and the launch payload don't keep
      // pointing at a persona that no longer matches the applied agent type.
      dispatch({ type: 'set_field', field: 'selectedPersonaId', value: null });
    }
    dispatch({
      type: 'set_agent_type',
      agentType: configured,
      defaultProviderId: getDefaultProviderForAgentType(configured),
      defaultModel: getDefaultModelForAgentType(configured),
      defaultEndpointRouteId: getDefaultEndpointForModel(
        configured,
        getDefaultModelForAgentType(configured),
      ),
    });
  }, [
    open,
    modelSupport,
    state.agentType,
    getDefaultProviderForAgentType,
    getDefaultModelForAgentType,
    getDefaultEndpointForModel,
    dispatch,
    selectedPersonaId,
    seededAgentType,
    restoredAgentTypeVersion,
  ]);

  // Sync selected model with model options changes
  useEffect(() => {
    if (!isMatrixDriven) return;

    const { agentType, model, selectedProviderId } = state;

    if (modelOptions.length === 0) {
      if (model) {
        dispatch({
          type: 'set_model',
          model: '',
          providerId: effectiveProviderId ?? '',
          defaultEndpointRouteId: null,
        });
      }
      return;
    }

    if (model && modelOptions.some((m) => m.id === model)) {
      const curatedDefaultModel = modelSupport.getDefaultModel(agentType);
      if (curatedDefaultModel && curatedDefaultModel !== model) {
        const currentProviderId = modelSupport.getProviderForModel(agentType, model);
        const curatedProviderId = modelSupport.getProviderForModel(agentType, curatedDefaultModel);
        if (currentProviderId !== curatedProviderId) {
          // A provider selected from the model editor is deliberate. Its
          // endpoint may be unavailable only because the user has not added
          // a provider key yet, so preserve the choice and defer that check
          // until launch instead of snapping back to the curated default.
          if (selectedProviderId === currentProviderId) return;
          const currentEndpoints = modelSupport.getEndpointsForProvider(
            agentType,
            currentProviderId,
          );
          const hasAvailableCurrentEndpoint = currentEndpoints.some((e) =>
            modelSupport.isEndpointAvailable(agentType, e.id),
          );
          if (!hasAvailableCurrentEndpoint) {
            dispatch({
              type: 'set_model',
              model: curatedDefaultModel,
              providerId: curatedProviderId,
              defaultEndpointRouteId: getDefaultEndpointForModel(agentType, curatedDefaultModel),
            });
            return;
          }
        }
      }
      return;
    }

    const curatedDefaultModel = modelSupport.getDefaultModel(agentType);
    const defaultModel =
      curatedDefaultModel && modelOptions.some((m) => m.id === curatedDefaultModel)
        ? curatedDefaultModel
        : (modelOptions[0]?.id ?? '');

    const defaultModelProviderId = modelSupport.getProviderForModel(agentType, defaultModel);
    dispatch({
      type: 'set_model',
      model: defaultModel,
      providerId: defaultModelProviderId,
      defaultEndpointRouteId: getDefaultEndpointForModel(agentType, defaultModel),
    });
  }, [
    isMatrixDriven,
    state,
    modelOptions,
    effectiveProviderId,
    modelSupport,
    getDefaultEndpointForModel,
    dispatch,
  ]);

  const markUserPickedAgentType = useCallback(() => {
    restoredAgentTypeRef.current = null;
    userPickedAgentTypeRef.current = true;
  }, []);

  const markRestoredAgentType = useCallback((agentType: AgentType) => {
    userPickedAgentTypeRef.current = false;
    restoredAgentTypeRef.current = agentType;
    bumpRestoredAgentTypeVersion();
  }, []);

  const resetAgentTypePreference = useCallback(() => {
    userPickedAgentTypeRef.current = false;
    restoredAgentTypeRef.current = null;
    bumpRestoredAgentTypeVersion();
  }, []);

  return { markUserPickedAgentType, markRestoredAgentType, resetAgentTypePreference };
}
